import React from 'react';
import {
  CaretUpIcon,
  CaretDownIcon,
  MinusIcon,
  PencilSimpleIcon,
  ChatCircleIcon,
  LinkIcon,
  ArrowSquareOutIcon,
  LinkBreakIcon,
  PushPinIcon,
  PushPinSlashIcon,
  SparkleIcon
} from '@phosphor-icons/react';
import { AssuranceLevel, dashboardAssuranceColors } from 'src/features/reporting/utils/color';
import {
  DASHBOARD_CHART_TILE_HEIGHT_CLASS,
  DASHBOARD_CLASSIC_TILE_HEIGHT_CLASS
} from 'src/features/reporting/utils/constants';
import MetricTileCard from 'src/features/reporting/components/MetricTileCard';
import { AssuranceGauge } from 'src/features/reporting/components/AssuranceGauge';
import Button from 'src/components/ui/Button';
import { AssuranceLevelPill, TileChart } from 'src/features/reporting/components/MetricTileCharts';
import type { ChartSpec } from 'src/components/charts';
import { DASHBOARD_TOUR_TARGETS } from 'src/features/reporting/dashboardTourTargets';
import { useChartTilesEnabled } from '../hooks/useDashboardTileStyle';

type Trend = 'up' | 'down' | 'static';

/** Group thousands with commas (en-GB). Dashboard tiles previously rendered raw numbers. */
function formatTileNumber(n: number, fractionDigits = 0): string {
  return n.toLocaleString('en-GB', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

type UpdateCadence = 'monthly' | 'quarterly' | 'weekly';

type MetricTileProps = {
  title: string;
  value: number | null;
  assurance: AssuranceLevel;
  trend: Trend;
  sparkline: (number | null)[];
  onDrilldown?: () => void;
  /** Chart tiles: open the SharePoint-style details pane. */
  onOpenDetails?: () => void;
  /** Classic tiles: open the comment popup. */
  onComment?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Classic tiles: open the edit modal. */
  onEdit?: () => void;
  commentCount?: number;
  onGoToOriginal?: () => void; // For linked cards - navigate to the original card
  onUnlink?: () => void; // For linked cards - remove/unlink the card
  hasLinkedCardAccess?: boolean; // Whether user has access to the linked card's dashboard
  suffix?: string;
  hero?: boolean;
  meta?: React.ReactNode;
  isGroupCard?: boolean; // Group cards only show assurance level, no numeric value
  isStatusCard?: boolean; // Status cards display Yes / No / — instead of numeric values
  isLinked?: boolean; // Is this a linked card (displays data from another card)
  // For group cards: how the assurance level is aggregated (rendered as a badge so viewers can tell)
  aggregationType?: 'min' | 'average' | null;
  // For linked cards: the source dashboard name (rendered as a subtitle)
  linkedSourceDashboardName?: string | null;
  // For group cards - assurance level history
  previousAssuranceLevel?: AssuranceLevel | null;
  historicalAssuranceLevels?: (AssuranceLevel | null)[];
  // For group cards - optional numeric display value (feature-flagged)
  groupNumericDisplayValue?: number | null;
  groupNumericDisplayTooltip?: string | null;
  // Update cadence for cadence-aware labels
  updateCadence?: UpdateCadence | null;
  /** The Vega-Lite spec from `dashboards/charts/chart_specs.py`. */
  chartSpec?: ChartSpec | null;
  // When the shown value is last-available in a coarser view (month/quarter)
  valueAsOfDisclaimer?: string | null;
  // Drag and drop support
  draggable?: boolean;
  nodeId?: string;
  onDragStart?: (nodeId: string) => void;
  onDragEnd?: () => void;
  // Drop zone support (for group cards)
  isDropZone?: boolean;
  onDrop?: (targetGroupId: string) => void;
  isDragOver?: boolean;
  onDragEnter?: (groupId: string) => void;
  onDragLeave?: () => void;
  // Show "can't drop" feedback
  showCantDropFeedback?: boolean;
  cantDropReason?: 'linked' | 'data-card';
  isPinned?: boolean;
  onTogglePin?: () => void;
  /** CompliMind Insight preview for the chart-layout hero card (full text opens in a modal). */
  heroInsight?: React.ReactNode;
  onShowInsight?: () => void;
};

// Numeric mapping for assurance levels (for trend calculation)
const LEVEL_TO_NUMERIC: Record<AssuranceLevel, number> = {
  None: 1,
  Limited: 2,
  Moderate: 3,
  Substantial: 4,
  High: 5,
  NaN: 0
};

const TrendIcon: React.FC<{ trend: Trend; size?: number }> = ({ trend, size = 28 }) => {
  const color = trend === 'up' ? '#16a34a' : trend === 'down' ? '#dc2626' : '#64748b';
  if (trend === 'up') return <CaretUpIcon size={size} color={color} weight="bold" />;
  if (trend === 'down') return <CaretDownIcon size={size} color={color} weight="bold" />;
  return <MinusIcon size={size} color={color} weight="bold" />;
};

const MiniSparkline: React.FC<{
  series: (number | null)[];
  height?: number;
  showDots?: boolean;
  highlightLast?: boolean;
  indicateMissingCurrent?: boolean;
}> = ({ series, height = 64, showDots = true, highlightLast = true, indicateMissingCurrent = false }) => {
  const chartSeries = React.useMemo(() => {
    if (!indicateMissingCurrent) return series;
    if (series.length === 0) return [null];
    if (series[series.length - 1] === null) return series;
    return [...series, null];
  }, [series, indicateMissingCurrent]);

  const numeric = chartSeries.filter((v): v is number => Number.isFinite(v as number));
  if (numeric.length === 0) return null;

  const max = Math.max(...numeric);
  const min = Math.min(...numeric);
  const range = Math.max(max - min, 1);
  // Keep plotted content away from SVG edges to avoid clipping.
  const CHART_MIN_X = 4;
  const CHART_MAX_X = 96;
  const CHART_MIN_Y = 6;
  const CHART_MAX_Y = 94;
  const chartWidth = CHART_MAX_X - CHART_MIN_X;
  const chartHeight = CHART_MAX_Y - CHART_MIN_Y;
  const missingMarkerY = CHART_MIN_Y + chartHeight / 2;
  const xForIndex = (i: number) =>
    chartSeries.length <= 1 ? CHART_MIN_X + chartWidth / 2 : CHART_MIN_X + (i / (chartSeries.length - 1)) * chartWidth;
  const yForValue = (v: number) => CHART_MAX_Y - ((v - min) / range) * chartHeight;

  const lastNumericIndex =
    [...chartSeries]
      .map((v, i) => (v == null ? -1 : i))
      .filter((i) => i >= 0)
      .pop() ?? -1;
  const numericPoints = chartSeries
    .map((v, i) => (v == null ? null : { index: i, x: xForIndex(i), y: yForValue(v) }))
    .filter((p): p is { index: number; x: number; y: number } => p !== null);

  const segments: { points: string }[] = [];
  let currentSegment: string[] = [];
  chartSeries.forEach((v, i) => {
    if (v == null) {
      if (currentSegment.length > 1) {
        segments.push({ points: currentSegment.join(' ') });
      }
      currentSegment = [];
      return;
    }
    currentSegment.push(`${xForIndex(i)},${yForValue(v)}`);
  });
  if (currentSegment.length > 1) {
    segments.push({ points: currentSegment.join(' ') });
  }

  const missingIndexes = chartSeries.map((v, i) => (v == null ? i : -1)).filter((i) => i >= 0);

  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }}>
      {segments.map((segment, i) => (
        <polyline key={`segment-${i}`} fill="none" stroke="#3b82f6" strokeWidth="3" points={segment.points} />
      ))}
      {showDots &&
        numericPoints.map(({ index, x, y }) => {
          const isLastNumeric = index === lastNumericIndex;
          const r = highlightLast && isLastNumeric ? 2.6 : 1.8;
          const fill = highlightLast && isLastNumeric ? '#1d4ed8' : '#3b82f6';
          return <circle key={index} cx={x} cy={y} r={r} fill={fill} />;
        })}
      {missingIndexes.map((i) => {
        const x = xForIndex(i);
        const y = missingMarkerY;
        return (
          <g key={`missing-${i}`}>
            <circle cx={x} cy={y} r={2.2} fill="#ffffff" stroke="#94a3b8" strokeWidth="1.4" />
            <line x1={x - 1.3} y1={y - 1.3} x2={x + 1.3} y2={y + 1.3} stroke="#94a3b8" strokeWidth="1.2" />
          </g>
        );
      })}
    </svg>
  );
};

// Assurance level trend heatmap for group cards
const AssuranceLevelTrend: React.FC<{
  levels: (AssuranceLevel | null)[];
  height?: number;
  colors: Record<AssuranceLevel, string>;
}> = ({ levels, height = 20, colors }) => {
  if (levels.length === 0) return null;

  return (
    <div className="flex items-center gap-px" style={{ height }}>
      {levels.map((level, i) => {
        const color = level ? colors[level] : '#e5e7eb';
        return (
          <div
            key={i}
            className="rounded-[2px]"
            style={{
              backgroundColor: color,
              height: '100%',
              width: 12
            }}
            title={level || 'No data'}
          />
        );
      })}
    </div>
  );
};

const TILE_STATS_ROW = 'flex items-center gap-2.5 flex-wrap min-h-[1.75rem] max-h-[3.25rem] min-w-0 overflow-hidden';

const MetricTile: React.FC<MetricTileProps> = ({
  title,
  value,
  assurance,
  trend,
  sparkline,
  onDrilldown,
  onOpenDetails,
  onComment,
  onEdit,
  commentCount = 0,
  onGoToOriginal,
  onUnlink,
  hasLinkedCardAccess = true,
  suffix = '',
  hero = false,
  meta,
  isGroupCard = false,
  isStatusCard = false,
  isLinked = false,
  aggregationType = null,
  linkedSourceDashboardName = null,
  previousAssuranceLevel,
  historicalAssuranceLevels = [],
  groupNumericDisplayValue = null,
  groupNumericDisplayTooltip = null,
  updateCadence = 'monthly',
  chartSpec = null,
  valueAsOfDisclaimer = null,
  draggable = false,
  nodeId,
  onDragStart,
  onDragEnd,
  isDropZone = false,
  onDrop,
  isDragOver = false,
  onDragEnter,
  onDragLeave,
  showCantDropFeedback = false,
  cantDropReason,
  isPinned = false,
  onTogglePin,
  heroInsight,
  onShowInsight
}) => {
  const useChartTiles = useChartTilesEnabled();
  const assuranceColors = dashboardAssuranceColors(useChartTiles);
  // A gauge frame has a numeric width. A line or bar stays `"container"` until this tile sizes it.
  const isDialSpec = typeof chartSpec?.width === 'number';
  const dialChart = chartSpec ? (
    <div className="flex h-full min-h-[64px] items-center justify-center">
      <AssuranceGauge spec={chartSpec} level={assurance} size={170} />
    </div>
  ) : null;

  // Determine period labels based on cadence
  const isQuarterly = updateCadence === 'quarterly';
  const isWeekly = updateCadence === 'weekly';
  const periodLabel = isQuarterly ? 'quarter' : isWeekly ? 'week' : 'month';
  const periodsLabel = isQuarterly ? 'quarters' : isWeekly ? 'weeks' : 'months';
  const previousValue = (sparkline.length > 1 ? sparkline[sparkline.length - 2] : null) as number | null;
  const currentValue = value;
  // Only calculate delta if both values exist; null means comparison unavailable
  const delta =
    currentValue != null && previousValue != null ? (currentValue as number) - (previousValue as number) : null;

  // Check if we have any historical data in the sparkline
  const hasHistoricalData = sparkline.some((v) => v !== null && Number.isFinite(v));

  // Calculate assurance level trend for group cards
  const assuranceLevelDelta = (() => {
    if (!previousAssuranceLevel || assurance === 'NaN') return 'static';
    const currentNumeric = LEVEL_TO_NUMERIC[assurance];
    const previousNumeric = LEVEL_TO_NUMERIC[previousAssuranceLevel];
    if (currentNumeric > previousNumeric) return 'up';
    if (currentNumeric < previousNumeric) return 'down';
    return 'static';
  })() as Trend;

  // Track if we should show the red feedback (only when actually hovering)
  const [isHoveringLinked, setIsHoveringLinked] = React.useState(false);
  const showRedFeedback = showCantDropFeedback && isHoveringLinked;

  // Reset hover state when showCantDropFeedback becomes false
  React.useEffect(() => {
    if (!showCantDropFeedback) {
      setIsHoveringLinked(false);
    }
  }, [showCantDropFeedback]);

  const cardContent = (
    <MetricTileCard
      // Linked cards get a persistent dashed border + subtle tint so they're
      // visually distinguishable from owned cards, not just a tiny icon.
      className={`${hero ? 'col-span-full !px-6 !py-5 !border-[3px] !border-secondary-blue/50' : useChartTiles ? DASHBOARD_CHART_TILE_HEIGHT_CLASS : DASHBOARD_CLASSIC_TILE_HEIGHT_CLASS} ${isLinked ? '!border-dashed !border-secondary-blue/60 !bg-blue-50/30' : ''} ${isDragOver ? '!border-blue-500 !border-[3px] !bg-blue-50 relative' : ''} ${showRedFeedback ? '!border-red-400 !border-[3px] !bg-red-50 relative' : ''} ${onTogglePin || (isPinned && !onTogglePin) ? 'group' : ''}`}
      compact={useChartTiles}
      onClick={onDrilldown}
      header={
        <div className="flex items-start justify-between gap-3 min-w-0 w-full">
          <div className="flex flex-col min-w-0 flex-1">
            <div
              className={`flex items-start gap-2 min-w-0 ${
                hero
                  ? 'text-xl md:text-2xl font-semibold'
                  : useChartTiles
                    ? 'text-sm font-medium leading-snug'
                    : 'text-lg font-medium leading-snug'
              }`}
            >
              {isLinked && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold text-secondary-blue bg-blue-100 border border-secondary-blue/40 px-2 py-0.5 rounded-full flex-shrink-0"
                  title="Linked card - displays data from another dashboard. Edits here change the source for everyone."
                >
                  <LinkIcon size={12} weight="bold" /> Linked
                </span>
              )}
              <span className={hero ? undefined : 'min-w-0 flex-1 line-clamp-2'} title={title}>
                {title}
              </span>
              {isGroupCard && aggregationType && (
                <span
                  className="ml-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-full flex-shrink-0"
                  title={
                    aggregationType === 'min'
                      ? 'Aggregated by minimum: this group takes the lowest assurance level among its children.'
                      : 'Aggregated by average: child assurance levels are averaged.'
                  }
                >
                  {aggregationType === 'min' ? 'min' : 'avg'}
                </span>
              )}
              {isDragOver && isDropZone && (
                <span className="ml-2 text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                  Move here
                </span>
              )}
              {showRedFeedback && (
                <span className="ml-2 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full flex-shrink-0">
                  {cantDropReason === 'linked'
                    ? "Can't move into linked card"
                    : cantDropReason === 'data-card'
                      ? "Can't move into data card"
                      : "Can't move here"}
                </span>
              )}
            </div>
            {isLinked && linkedSourceDashboardName && (
              <div
                className="mt-0.5 text-xs text-secondary-blue/80 italic truncate"
                title={`Linked from "${linkedSourceDashboardName}"`}
              >
                Linked from "{linkedSourceDashboardName}"
              </div>
            )}
            {meta && <div className="mt-1 text-xs text-gray-500 truncate">{meta}</div>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {onTogglePin ? (
              <Button
                variant="ghost"
                size="md"
                icon={isPinned ? PushPinSlashIcon : PushPinIcon}
                className={isPinned ? undefined : 'opacity-0 group-hover:opacity-100'}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePin();
                }}
                title={isPinned ? 'Unpin card' : 'Pin card to top'}
                aria-label={isPinned ? 'Unpin card' : 'Pin card to top'}
              />
            ) : (
              isPinned && <PushPinIcon size={14} weight="fill" className="text-blue-500" aria-label="Pinned card" />
            )}
            {/* For linked cards: show go-to-original and unlink buttons */}
            {isLinked && onUnlink && (
              <Button
                variant="ghost"
                size="md"
                icon={LinkBreakIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onUnlink();
                }}
                aria-label="Remove linked card"
                title="Remove linked card"
              />
            )}
            {/* Only show "Go to original" if user has access to the linked card's dashboard */}
            {isLinked && onGoToOriginal && hasLinkedCardAccess && (
              <Button
                variant="ghost"
                size="md"
                icon={ArrowSquareOutIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  onGoToOriginal();
                }}
                aria-label="Go to original card"
                title="Go to original card"
              />
            )}
            {useChartTiles ? (
              onOpenDetails && (
                <span className="relative inline-flex">
                  <Button
                    variant="ghost"
                    size="md"
                    icon={ArrowSquareOutIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDetails();
                    }}
                    aria-label="See details"
                    title="See details"
                  />
                  {commentCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-secondary-blue text-white text-[10px] font-semibold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 pointer-events-none">
                      {commentCount > 99 ? '99+' : commentCount}
                    </span>
                  )}
                </span>
              )
            ) : (
              <>
                {onComment && (
                  <span className="relative inline-flex">
                    <Button
                      variant="ghost"
                      size="md"
                      icon={ChatCircleIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        onComment(e);
                      }}
                      aria-label="Comments"
                      title="Comments"
                    />
                    {commentCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-secondary-blue text-white text-[10px] font-semibold rounded-full min-w-[16px] h-4 flex items-center justify-center px-0.5 pointer-events-none">
                        {commentCount > 99 ? '99+' : commentCount}
                      </span>
                    )}
                  </span>
                )}
                {!isLinked && onEdit && (
                  <Button
                    variant="ghost"
                    size="md"
                    icon={PencilSimpleIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                    aria-label="Edit"
                  />
                )}
              </>
            )}
          </div>
        </div>
      }
    >
      <div className={hero ? 'p-2 md:p-3 h-full min-h-0 flex flex-col' : 'h-full min-h-0 flex flex-col'}>
        {isStatusCard ? (
          useChartTiles ? (
            <div className="flex flex-col gap-2 h-full min-h-0">
              <div className={TILE_STATS_ROW}>
                {currentValue === null ? (
                  <span className="text-sm text-gray-400 italic">No data</span>
                ) : (
                  <span className={`${hero ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900 leading-none shrink-0`}>
                    {currentValue === 1 ? 'Yes' : currentValue === 0 ? 'No' : '—'}
                  </span>
                )}
                {assurance !== 'NaN' && <AssuranceLevelPill level={assurance} />}
                {assurance === 'NaN' && currentValue !== null && (
                  <span className="text-xs text-gray-400 italic">No assurance level</span>
                )}

                {currentValue !== null && (
                  <>
                    <div className="w-px h-5 bg-gray-200 flex-shrink-0" aria-hidden />
                    <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      {previousAssuranceLevel && previousAssuranceLevel !== 'NaN' ? (
                        <>
                          <TrendIcon trend={assuranceLevelDelta} size={16} />
                          <span className="text-sm font-semibold text-gray-700">
                            {assuranceLevelDelta === 'up'
                              ? 'Improved'
                              : assuranceLevelDelta === 'down'
                                ? 'Declined'
                                : 'Stable'}
                          </span>
                          <span className="text-[11px] text-gray-500 whitespace-nowrap shrink-0">
                            vs last {periodLabel}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No prior data</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="flex-1 min-h-0 min-w-0">
                {isDialSpec ? (
                  dialChart
                ) : !chartSpec ? (
                  <div className="flex items-center justify-center h-full min-h-[64px]">
                    <span className="text-xs text-gray-400 italic">No historical data</span>
                  </div>
                ) : (
                  <TileChart spec={chartSpec} height={96} fill ariaLabel="Yes or no history" />
                )}
              </div>
              {valueAsOfDisclaimer && (
                <div
                  className="text-[11px] text-amber-700 truncate shrink-0"
                  title="Later periods in this view have no data"
                >
                  {valueAsOfDisclaimer}
                </div>
              )}
            </div>
          ) : (
            // Status cards: Show Yes / No / — with derived assurance level
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch min-h-[120px]">
              {/* Left: Change vs last period */}
              <div className="flex flex-col justify-between items-start h-full">
                <div className="flex items-center gap-2">
                  {currentValue === null ? (
                    <span className="text-sm text-gray-400 italic">No data</span>
                  ) : previousAssuranceLevel && previousAssuranceLevel !== 'NaN' ? (
                    <>
                      <TrendIcon trend={assuranceLevelDelta} size={32} />
                      <span className="text-sm font-medium text-gray-700">
                        {assuranceLevelDelta === 'up'
                          ? 'Improved'
                          : assuranceLevelDelta === 'down'
                            ? 'Declined'
                            : 'Stable'}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm text-gray-400 italic">No prior data</span>
                  )}
                </div>
                <div className="text-left text-[11px] text-gray-500">vs last {periodLabel}</div>
              </div>

              {/* Center: Yes/No value and assurance gauge */}
              <div className="flex flex-col justify-between items-center h-full">
                {currentValue === null ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm font-medium text-gray-400 text-center italic">No assurance level data</div>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      {chartSpec && <AssuranceGauge spec={chartSpec} level={assurance} size={130} />}
                    </div>
                    <div className="text-xl font-semibold text-gray-900 text-center" style={{ marginTop: -4 }}>
                      {currentValue === 1 ? 'Yes' : currentValue === 0 ? 'No' : '—'}
                    </div>
                  </div>
                )}
                <div
                  className={`text-center text-[11px] ${valueAsOfDisclaimer ? 'text-amber-700' : 'text-gray-500'}`}
                  title={valueAsOfDisclaimer ? 'Later periods in this view have no data' : undefined}
                >
                  {valueAsOfDisclaimer ?? 'Current'}
                </div>
              </div>

              {/* Right: Historical assurance trend */}
              <div className="flex flex-col justify-between items-end h-full">
                <div className="h-[72px] flex items-center">
                  {historicalAssuranceLevels.length > 0 ? (
                    <AssuranceLevelTrend levels={historicalAssuranceLevels} height={36} colors={assuranceColors} />
                  ) : (
                    <div className="text-[10px] text-gray-400">No history</div>
                  )}
                </div>
                <div className="text-right text-[11px] text-gray-500">
                  {historicalAssuranceLevels.length > 0 ? `${historicalAssuranceLevels.length} ${periodsLabel}` : ''}
                </div>
              </div>
            </div>
          )
        ) : isGroupCard ? (
          useChartTiles ? (
            <div className={`flex gap-5 h-full min-h-0 ${hero ? 'flex-col sm:flex-row sm:items-stretch' : 'flex-col'}`}>
              <div className={`flex flex-col gap-2 min-w-0 min-h-0 ${hero ? 'sm:flex-1' : 'w-full h-full'}`}>
                <div className={TILE_STATS_ROW}>
                  {assurance === 'NaN' ? (
                    <span className="text-sm text-gray-400 italic">No data</span>
                  ) : (
                    <AssuranceLevelPill level={assurance} />
                  )}

                  {groupNumericDisplayValue !== null && groupNumericDisplayValue !== undefined && (
                    <span
                      className="relative group/numval text-sm font-semibold text-gray-500 lining-nums cursor-default shrink-0"
                      title={groupNumericDisplayTooltip ?? undefined}
                    >
                      {formatTileNumber(groupNumericDisplayValue, Number.isInteger(groupNumericDisplayValue) ? 0 : 1)}
                      {groupNumericDisplayTooltip && (
                        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 hidden group-hover/numval:block w-max max-w-[200px]">
                          <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 text-center leading-snug shadow-lg">
                            {groupNumericDisplayTooltip}
                          </div>
                          <div className="w-2 h-2 bg-gray-800 rotate-45 mx-auto -mt-1" />
                        </div>
                      )}
                    </span>
                  )}

                  {assurance !== 'NaN' && (
                    <>
                      <div className="w-px h-5 bg-gray-200 flex-shrink-0" aria-hidden />
                      <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                        {previousAssuranceLevel && previousAssuranceLevel !== 'NaN' ? (
                          <>
                            <TrendIcon trend={assuranceLevelDelta} size={16} />
                            <span className="text-sm font-semibold text-gray-700">
                              {assuranceLevelDelta === 'up'
                                ? 'Improved'
                                : assuranceLevelDelta === 'down'
                                  ? 'Declined'
                                  : 'Stable'}
                            </span>
                            <span className="text-[11px] text-gray-500 whitespace-nowrap shrink-0">
                              vs last {periodLabel}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No prior data</span>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="min-w-0 min-h-0 w-full flex-1">
                  {isDialSpec ? (
                    dialChart
                  ) : !chartSpec ? (
                    <div className="flex items-center justify-center h-full min-h-[64px]">
                      <span className="text-xs text-gray-400 italic">No historical data</span>
                    </div>
                  ) : (
                    <TileChart spec={chartSpec} height={96} fill ariaLabel="Assurance level history" />
                  )}
                </div>
              </div>

              {hero && (
                <div
                  className={`${DASHBOARD_TOUR_TARGETS.complimindInsight} flex-1 min-w-0 rounded-lg border border-border-default bg-blue-50/40 px-4 py-3 flex flex-col`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-1.5 mb-1.5 shrink-0">
                    <SparkleIcon size={16} weight="fill" className="text-secondary-blue shrink-0" />
                    <span className="text-sm font-medium text-content-default">CompliMind Insight</span>
                  </div>
                  <div className="flex-1 min-h-0 max-h-[8.5rem] overflow-hidden">{heroInsight}</div>
                  {onShowInsight && (
                    <button
                      type="button"
                      className="self-start mt-2 shrink-0 text-sm font-medium text-secondary-blue hover:underline"
                      onClick={onShowInsight}
                    >
                      Show more
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Group cards: Show assurance level with gauge and trend information
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch min-h-[120px]">
              {/* Left: Trend vs last month */}
              <div className="flex flex-col justify-between items-start h-full">
                <div>
                  {assurance === 'NaN' ? (
                    <span className="text-sm text-gray-400 italic">No data</span>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <TrendIcon trend={assuranceLevelDelta} size={32} />
                        <span className="text-sm font-medium text-gray-700">
                          {assuranceLevelDelta === 'up'
                            ? 'Improved'
                            : assuranceLevelDelta === 'down'
                              ? 'Declined'
                              : 'Stable'}
                        </span>
                      </div>
                      {previousAssuranceLevel && previousAssuranceLevel !== 'NaN' && (
                        <div className="mt-1 text-xs text-gray-500">from {previousAssuranceLevel}</div>
                      )}
                    </>
                  )}
                </div>
                <div className="text-left text-[11px] text-gray-500">vs last {periodLabel}</div>
              </div>

              {/* Center: Gauge, assurance level, and optional numeric value */}
              <div className="flex flex-col justify-between items-center h-full">
                {assurance === 'NaN' ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm font-medium text-gray-400 text-center italic">No assurance level data</div>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      {chartSpec && <AssuranceGauge spec={chartSpec} level={assurance} size={130} />}
                    </div>
                    {groupNumericDisplayValue !== null && groupNumericDisplayValue !== undefined && (
                      <div className="relative group/numval flex justify-center" style={{ marginTop: -4 }}>
                        <span className="text-xl font-semibold text-gray-900 cursor-default">
                          {formatTileNumber(
                            groupNumericDisplayValue,
                            Number.isInteger(groupNumericDisplayValue) ? 0 : 1
                          )}
                        </span>
                        {groupNumericDisplayTooltip && (
                          <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-50 hidden group-hover/numval:block w-max max-w-[200px]">
                            <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 text-center leading-snug shadow-lg">
                              {groupNumericDisplayTooltip}
                            </div>
                            <div className="w-2 h-2 bg-gray-800 rotate-45 mx-auto -mt-1" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="text-center text-[11px] text-gray-500">Aggregated Level</div>
              </div>

              {/* Right: Historical trend heatmap */}
              <div className="flex flex-col justify-between items-end h-full">
                <div className="h-[72px] flex items-center">
                  {historicalAssuranceLevels.length > 0 ? (
                    <AssuranceLevelTrend levels={historicalAssuranceLevels} height={36} colors={assuranceColors} />
                  ) : (
                    <div className="text-[10px] text-gray-400">No history</div>
                  )}
                </div>
                <div className="text-right text-[11px] text-gray-500">
                  {historicalAssuranceLevels.length > 0 ? `${historicalAssuranceLevels.length} ${periodsLabel}` : ''}
                </div>
              </div>
            </div>
          )
        ) : useChartTiles ? (
          <div className="flex flex-col gap-2 h-full min-h-0">
            <div className={TILE_STATS_ROW}>
              {currentValue === null && !hasHistoricalData ? (
                <span className="text-sm text-gray-400 italic">No data</span>
              ) : currentValue === null ? (
                <span className="text-sm text-gray-400 italic">No current value</span>
              ) : (
                <span
                  className={`${hero ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900 leading-none lining-nums shrink-0`}
                >
                  {formatTileNumber(Math.round(currentValue))}
                  {suffix}
                </span>
              )}
              {assurance !== 'NaN' && <AssuranceLevelPill level={assurance} />}
              {assurance === 'NaN' && (currentValue !== null || hasHistoricalData) && (
                <span className="text-xs text-gray-400 italic">No assurance level</span>
              )}

              {(currentValue !== null || hasHistoricalData) && (
                <>
                  <div className="w-px h-5 bg-gray-200 flex-shrink-0" aria-hidden />
                  <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                    {currentValue === null ? (
                      <span className="text-xs text-gray-400 italic">No current data</span>
                    ) : delta !== null ? (
                      <>
                        <TrendIcon trend={trend} size={16} />
                        <span className="text-sm font-semibold text-gray-700 lining-nums shrink-0">
                          {delta === 0
                            ? '0'
                            : delta > 0
                              ? `+${formatTileNumber(Math.round(delta))}${suffix}`
                              : `${formatTileNumber(Math.round(delta))}${suffix}`}
                        </span>
                        <span className="text-[11px] text-gray-500 whitespace-nowrap">vs last {periodLabel}</span>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No prior data</span>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex-1 min-h-0 min-w-0">
              {isDialSpec ? (
                dialChart
              ) : !chartSpec ? (
                <div className="flex items-center justify-center h-full min-h-[64px]">
                  <span className="text-xs text-gray-400 italic">No historical data</span>
                </div>
              ) : (
                <TileChart spec={chartSpec} height={96} fill ariaLabel="Value trend over time" />
              )}
            </div>
            {valueAsOfDisclaimer && (
              <div
                className="text-[11px] text-amber-700 truncate shrink-0"
                title="Later periods in this view have no data"
              >
                {valueAsOfDisclaimer}
              </div>
            )}
          </div>
        ) : (
          // Data cards: Show full metrics with gauge, value, trend, and sparkline
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch min-h-[120px]">
            {/* Left: Trend vs last period */}
            <div className="flex flex-col justify-between items-start h-full">
              <div className="flex items-center gap-2">
                {currentValue === null && !hasHistoricalData ? (
                  <span className="text-sm text-gray-400 italic">No data</span>
                ) : currentValue === null && hasHistoricalData ? (
                  <span className="text-sm text-gray-400 italic">No current data</span>
                ) : delta !== null ? (
                  <>
                    <TrendIcon trend={trend} size={32} />
                    <span className="text-sm font-medium text-gray-700">
                      {delta === 0
                        ? 'Stable'
                        : delta > 0
                          ? `+${formatTileNumber(Math.round(delta))}${suffix}`
                          : `${formatTileNumber(Math.round(delta))}${suffix}`}
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-gray-400 italic">No prior data</span>
                )}
              </div>
              <div className="text-left text-[11px] text-gray-500">vs last {periodLabel}</div>
            </div>

            {/* Center: Gauge, value and assurance */}
            <div className="flex flex-col justify-between items-center h-full">
              {currentValue === null && !hasHistoricalData ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm font-medium text-gray-400 text-center italic">No data</div>
                </div>
              ) : assurance === 'NaN' ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm font-medium text-gray-400 text-center italic">No assurance level data</div>
                </div>
              ) : (
                <div>
                  <div className="relative">
                    {chartSpec && <AssuranceGauge spec={chartSpec} level={assurance} size={130} />}
                  </div>
                  {currentValue !== null ? (
                    <div className="text-xl font-semibold text-gray-900 text-center" style={{ marginTop: -4 }}>
                      {formatTileNumber(Math.round(currentValue))}
                      {suffix}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 text-center italic" style={{ marginTop: -4 }}>
                      No current value
                    </div>
                  )}
                </div>
              )}
              <div
                className={`text-center text-[11px] ${valueAsOfDisclaimer ? 'text-amber-700' : 'text-gray-500'}`}
                title={valueAsOfDisclaimer ? 'Later periods in this view have no data' : undefined}
              >
                {valueAsOfDisclaimer ?? 'Current'}
              </div>
            </div>

            {/* Right: trend sparkline */}
            <div className="flex flex-col justify-between items-end h-full">
              <div className="w-full h-[72px]">
                {!hasHistoricalData ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-xs text-gray-400 italic">No historical data</span>
                  </div>
                ) : (
                  <MiniSparkline
                    series={sparkline}
                    height={72}
                    showDots={true}
                    highlightLast={true}
                    indicateMissingCurrent={currentValue === null}
                  />
                )}
              </div>
              <div className="text-right text-[11px] text-gray-500">
                {hasHistoricalData ? `last ${sparkline.length} ${periodsLabel}` : ''}
              </div>
            </div>
          </div>
        )}
      </div>
    </MetricTileCard>
  );

  // If both draggable and drop zone (shouldn't happen, but handle it)
  // If draggable, wrap in a div that handles drag events
  // If drop zone (group card), wrap in a div that handles drop events
  // If showing can't drop feedback, wrap to handle drag events
  const isDraggable = draggable && !hero && nodeId;
  const isDroppable = isDropZone && isGroupCard && !isLinked && nodeId;
  const needsWrapper = isDraggable || isDroppable || showCantDropFeedback;

  if (needsWrapper) {
    return (
      <div
        draggable={!!isDraggable}
        className={`${hero ? '' : 'h-full min-w-0'} ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onDragStart={
          isDraggable
            ? (e) => {
                e.stopPropagation();
                if (onDragStart) {
                  onDragStart(nodeId!);
                }
                // Set drag effect
                e.dataTransfer.effectAllowed = 'move';
                // Add a slight transparency to the dragged element
                e.currentTarget.style.opacity = '0.5';
              }
            : undefined
        }
        onDragEnd={
          isDraggable
            ? (e) => {
                e.stopPropagation();
                // Reset opacity
                e.currentTarget.style.opacity = '1';
                if (onDragEnd) {
                  onDragEnd();
                }
              }
            : undefined
        }
        onDragOver={
          isDroppable
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'move';
              }
            : showCantDropFeedback
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'none';
                }
              : undefined
        }
        onDragEnter={
          isDroppable || showCantDropFeedback
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (showCantDropFeedback) {
                  setIsHoveringLinked(true);
                }
                if (onDragEnter) {
                  onDragEnter(nodeId!);
                }
              }
            : undefined
        }
        onDragLeave={
          isDroppable || showCantDropFeedback
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                // Only trigger leave if we're actually leaving the element (not entering a child)
                if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) {
                  if (showCantDropFeedback) {
                    setIsHoveringLinked(false);
                  }
                  if (onDragLeave) {
                    onDragLeave();
                  }
                }
              }
            : undefined
        }
        onDrop={
          isDroppable
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onDrop) {
                  onDrop(nodeId!);
                }
              }
            : undefined
        }
      >
        {cardContent}
      </div>
    );
  }

  return <div className={hero ? undefined : 'h-full min-w-0'}>{cardContent}</div>;
};

export default MetricTile;
